import React, { useEffect, useMemo, useRef } from 'react';
import { useTabActivity } from '../../contexts/TabActivityContext';
import { MessageSquare } from 'lucide-react';
import { MessageListProps, ToolCallInfo } from './types';
import HumanMessage from './HumanMessage';
import AssistantMessage from './AssistantMessage';
import { AgenticAssistantMessageView } from '../../features/agentic-search/types';

const toolTraceToToolCalls = (message: AgenticAssistantMessageView): ToolCallInfo[] => (
  (message.toolTrace || []).map((toolTrace, index) => ({
    id: `${message.turnId}:${index}:${toolTrace.tool_name}`,
    toolName: toolTrace.tool_name,
    arguments: toolTrace.arguments || {},
    status: toolTrace.error ? 'error' : 'completed',
    resultSummary: toolTrace.result_summary || {},
    startedAt: toolTrace.started_at || message.timestamp,
    completedAt: toolTrace.completed_at || null,
    error: toolTrace.error || null,
  }))
);

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  liveTurn,
  onResultClick,
  onSelectResultSet,
  onJumpToResults,
  scrollKey,
  getScrollPosition,
  setScrollPosition,
}) => {
  const { isActive } = useTabActivity();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wasNearBottomRef = useRef(true);
  const isRestoringScrollRef = useRef(false);
  const previousRenderStateRef = useRef({
    messageCount: 0,
    liveToolCallCount: 0,
    liveStatusMessage: '',
  });

  const checkIfNearBottom = () => {
    const container = containerRef.current;
    if (!container) return true;
    const threshold = 100;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  };

  const handleScroll = () => {
    wasNearBottomRef.current = checkIfNearBottom();
    if (scrollKey && containerRef.current) {
      setScrollPosition?.(scrollKey, containerRef.current.scrollTop);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !scrollKey) return undefined;
    isRestoringScrollRef.current = true;
    const frame = window.requestAnimationFrame(() => {
      container.scrollTop = getScrollPosition?.(scrollKey) || 0;
      wasNearBottomRef.current = checkIfNearBottom();
      isRestoringScrollRef.current = false;
    });
    return () => {
      window.cancelAnimationFrame(frame);
      isRestoringScrollRef.current = false;
      setScrollPosition?.(scrollKey, container.scrollTop);
    };
  }, [getScrollPosition, scrollKey, setScrollPosition]);

  useEffect(() => {
    if (!isActive) return;
    const container = containerRef.current;
    if (isRestoringScrollRef.current || !wasNearBottomRef.current || !container) {
      previousRenderStateRef.current = {
        messageCount: messages.length,
        liveToolCallCount: liveTurn?.toolCalls.length || 0,
        liveStatusMessage: liveTurn?.agentState.message || '',
      };
      return;
    }

    const previous = previousRenderStateRef.current;
    const next = {
      messageCount: messages.length,
      liveToolCallCount: liveTurn?.toolCalls.length || 0,
      liveStatusMessage: liveTurn?.agentState.message || '',
    };

    if (
      previous.messageCount !== next.messageCount
      || previous.liveToolCallCount !== next.liveToolCallCount
    ) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else if (previous.liveStatusMessage !== next.liveStatusMessage) {
      container.scrollTop = container.scrollHeight;
    }

    previousRenderStateRef.current = next;
  }, [isActive, liveTurn?.agentState.message, liveTurn?.toolCalls.length, messages.length]);

  const renderedMessages = useMemo(() => (
    messages.map((message) => {
      if (message.role === 'user') {
        return (
          <HumanMessage
            key={message.id}
            content={message.content}
            timestamp={message.timestamp}
          />
        );
      }

      return (
        <AssistantMessage
          key={message.id}
          turnId={message.turnId}
          content={message.content}
          timestamp={message.timestamp}
          toolCalls={toolTraceToToolCalls(message)}
          resultSets={message.resultSets}
          activeResultSetId={message.activeResultSetId}
          onSelectResultSet={onSelectResultSet}
          onJumpToResults={onJumpToResults}
          onResultClick={onResultClick}
          errorMessage={message.errorMessage}
        />
      );
    })
  ), [messages, onJumpToResults, onResultClick, onSelectResultSet]);

  const isEmpty = messages.length === 0 && !liveTurn;

  return (
    <div
      className="agentic-message-list"
      role="log"
      aria-label="Conversation messages"
      ref={containerRef}
      onScroll={handleScroll}
    >
      {isEmpty ? (
        <div className="chat-welcome">
          <MessageSquare size={48} className="welcome-icon" />
          <h3>Welcome to Agentic Search</h3>
          <p>Ask about your videos, and the agent will search and reason across your scoped index.</p>
        </div>
      ) : (
        <>
          {renderedMessages}

          {liveTurn && (
            <AssistantMessage
              turnId={liveTurn.turnId || liveTurn.optimisticUserMessage.turnId}
              content=""
              toolCalls={liveTurn.toolCalls}
              resultSets={[]}
              onSelectResultSet={onSelectResultSet}
              onJumpToResults={onJumpToResults}
              onResultClick={onResultClick}
              isStreaming={true}
              streamingLabel={liveTurn.agentState.message}
            />
          )}
        </>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
